import { useCallback, useEffect, useRef, useState } from 'react';
import tips from '../assets/waitingTips.json';

const INITIAL_DELAY = 8_000;     // ms - show default banner for 8 seconds on first load
const SUBSEQUENT_DELAY = 6_000;  // ms - show default banner for 6 seconds on subsequent loads
const TIP_DURATION = 10_000;     // ms - show each tip for 10 seconds
const REMINDER_DURATION = 5_000; // ms - show reminder message for 5 seconds
const FADE_DURATION = 700;       // ms - fade transition duration
const TIPS_BEFORE_REMINDER = 5;  // number of tips before showing reminder

interface LoadingTipsState {
  currentContent: string | null;
  isFading: boolean;
}

export const useLoadingTips = (loading: boolean, isFirstLoad: boolean = false): LoadingTipsState => {
  const [currentContent, setCurrentContent] = useState<string | null>(null);
  const [isFading, setIsFading] = useState<boolean>(false);
  const tipCounterRef = useRef<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fadeTransition = useCallback(async (newContent: string | null, delay: number = TIP_DURATION, isReminder: boolean = false) => {
    // Start fade out
    setIsFading(true);
    
    // Wait for fade out to complete
    await new Promise(resolve => setTimeout(resolve, FADE_DURATION));
    
    // Change content
    setCurrentContent(newContent);
    
    // Start fade in
    setIsFading(false);
    
    // Schedule next transition
    timeoutRef.current = setTimeout(() => {
      if (isReminder) {
        // After reminder, resume tips
        fadeTransition(randomTip(), TIP_DURATION);
      } else if (newContent !== null) {
        // Regular tip logic
        tipCounterRef.current += 1;
        
        // Every 5 tips, show reminder message
        if (tipCounterRef.current % TIPS_BEFORE_REMINDER === 0) {
          fadeTransition(null, REMINDER_DURATION, true); // Show reminder
        } else {
          fadeTransition(randomTip(), TIP_DURATION); // Show next tip
        }
      }
    }, delay);
  }, []);

  useEffect(() => {
    // Clear any existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearTimeout(intervalRef.current);
      intervalRef.current = null;
    }

    if (!loading) {
       // Reset when loading stops
       setCurrentContent(null);
       setIsFading(false);
       tipCounterRef.current = 0;
       return;
     }

     // Reset states when loading starts
     setIsFading(false);
     tipCounterRef.current = 0;

     if (isFirstLoad) {
       // First load: show default message for 8 seconds, then start tips
       setCurrentContent(null);
       timeoutRef.current = setTimeout(() => {
         fadeTransition(randomTip(), TIP_DURATION);
       }, INITIAL_DELAY);
     } else {
       // Subsequent loads: show default message for 6 seconds, then start tips
       setCurrentContent(null);
       timeoutRef.current = setTimeout(() => {
         fadeTransition(randomTip(), TIP_DURATION);
       }, SUBSEQUENT_DELAY);
     }

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [loading, isFirstLoad, fadeTransition]);

  return { currentContent, isFading };
};

function randomTip(): string {
  return tips[Math.floor(Math.random() * tips.length)].text;
}
